import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Edit, Filter, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2, Users, UserCheck, Wallet, Eye, Bell } from "lucide-react";
import { useGetCustomersQuery, useGetCustomerWalletQuery, useUpdateCustomerUpiMutation, useUpdateCustomerWalletMutation } from "../../state/api";
import CustomerWalletSidebar, { formatWallet } from "../../components/CustomerWalletSidebar";
import SendNotificationDialog from "../../components/SendNotificationDialog";

const formatPlatform = (deviceType) => {
  if (!deviceType) return "—";
  const value = String(deviceType).toLowerCase();
  if (value.includes("ios")) return "iOS";
  if (value.includes("android")) return "Android";
  return deviceType;
};

const formatAppVersion = (customer) => {
  const version = customer?.appVersion;
  const build = customer?.buildNumber;
  if (version && build) return `${version} (${build})`;
  if (version) return version;
  if (build) return `build ${build}`;
  return "—";
};

const formatRegistrationDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString();
};

const formatLastActive = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const getCustomerName = (customer) =>
  customer?.name || customer?.fullName || customer?.firstName || "N/A";

const getCustomerPhone = (customer) =>
  customer?.phone || customer?.phoneNumber || "—";

const StatusBadge = ({ status }) => {
  if (status === "ACTIVE") {
    return (
      <span className="inline-block bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-2 py-1 rounded text-xs">
        ACTIVE
      </span>
    );
  }
  if (status === "DELETED") {
    return (
      <span className="inline-block bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-2 py-1 rounded text-xs">
        DELETED
      </span>
    );
  }
  return (
    <span className="inline-block bg-gray-100 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400 px-2 py-1 rounded text-xs">
      {status || "N/A"}
    </span>
  );
};

const DetailRow = ({ label, value, mono = false }) => (
  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
    <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
    <span
      className={`text-sm text-gray-900 dark:text-white sm:text-right break-all ${
        mono ? "font-mono" : ""
      }`}
    >
      {value ?? "—"}
    </span>
  </div>
);

const Customers = () => {
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  const { data: customersResponse, isLoading, error, refetch, isFetching } = useGetCustomersQuery({ 
    page: currentPage, 
    limit: itemsPerPage,
    search: debouncedSearch || undefined,
    status: statusFilter,
  });

  const [updateCustomerUpi, { isLoading: isSavingUpi }] = useUpdateCustomerUpiMutation();
  const [updateCustomerWallet, { isLoading: isSavingWallet }] = useUpdateCustomerWalletMutation();

  // Handle different data structures from the API
  const customers = customersResponse?.users || customersResponse?.data || customersResponse || [];

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isWalletUpdateOpen, setIsWalletUpdateOpen] = useState(false);
  const [walletSidebarOpen, setWalletSidebarOpen] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationTarget, setNotificationTarget] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [walletCustomer, setWalletCustomer] = useState(null);

  // Form states
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerStatus, setCustomerStatus] = useState("ACTIVE");
  const [customerUpiId, setCustomerUpiId] = useState("");
  const [customerWalletAmount, setCustomerWalletAmount] = useState("");
  const [walletOperation, setWalletOperation] = useState("ADD");
  const [walletNote, setWalletNote] = useState("");

  const {
    data: walletData,
    isLoading: walletLoading,
    isFetching: walletFetching,
    error: walletError,
    refetch: refetchWallet,
  } = useGetCustomerWalletQuery(walletCustomer?._id, {
    skip: !walletCustomer?._id || !walletSidebarOpen,
  });

  // API returns paginated, server-filtered results
  const currentCustomers = customers;

  // Pagination calculations - using API pagination data
  const totalItems = customersResponse?.pagination?.totalUsers ?? 0;
  const totalPages = customersResponse?.pagination?.totalPages ?? 0;
  
  // Calculate start and end index for display
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToFirstPage = () => {
    setCurrentPage(1);
  };

  const goToLastPage = () => {
    setCurrentPage(totalPages);
  };

  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Dialog handlers
  const handleCreateCustomer = () => {
    // Add customer creation logic here
    console.log("Creating customer:", { customerName, customerEmail, customerPhone, customerStatus });
    setIsCreateDialogOpen(false);
    resetForm();
  };

  const handleEditCustomer = async () => {
    if (!selectedCustomer?._id) return;
    try {
      await updateCustomerUpi({
        userId: selectedCustomer._id,
        upiId: customerUpiId,
      }).unwrap();
      setIsEditDialogOpen(false);
      resetForm();
    } catch (err) {
      const raw = err?.data?.message;
      const msg = Array.isArray(raw) ? raw.join(", ") : raw || err?.error || "Failed to save UPI";
      window.alert(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  };

  const handleUpdateWallet = async () => {
    const target = walletCustomer || selectedCustomer;
    if (!target?._id) return;
    const parsed = parseFloat(String(customerWalletAmount).trim());
    if (!Number.isFinite(parsed) || parsed <= 0) {
      window.alert("Enter a valid amount greater than zero.");
      return;
    }
    const note = String(walletNote || "").trim();
    if (!note) {
      window.alert("Please enter a note. This note is shown to the user in the app.");
      return;
    }
    try {
      const result = await updateCustomerWallet({
        userId: target._id,
        amount: parsed,
        operation: walletOperation,
        note,
      }).unwrap();
      const actionLabel = walletOperation === "ADD" ? "Added" : "Deducted";
      window.alert(
        `${actionLabel} ${formatWallet(result.amount)}. New balance: ${formatWallet(result.balance)} (was ${formatWallet(result.previousBalance)}).`
      );
      setIsWalletUpdateOpen(false);
      setCustomerWalletAmount("");
      setWalletOperation("ADD");
      setWalletNote("");
      if (walletSidebarOpen) {
        refetchWallet();
      }
    } catch (err) {
      const raw = err?.data?.message;
      const msg = Array.isArray(raw) ? raw.join(", ") : raw || err?.error || "Failed to update wallet";
      window.alert(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  };

  const handleOpenWalletSidebar = (customer) => {
    setWalletCustomer(customer);
    setWalletSidebarOpen(true);
  };

  const handleCloseWalletSidebar = () => {
    setWalletSidebarOpen(false);
    setWalletCustomer(null);
  };

  const handleOpenWalletUpdate = () => {
    const target = walletCustomer;
    if (!target) return;
    setCustomerWalletAmount("");
    setWalletOperation("ADD");
    setWalletNote("");
    setIsWalletUpdateOpen(true);
  };

  const handleCloseWalletUpdate = () => {
    setIsWalletUpdateOpen(false);
    setCustomerWalletAmount("");
    setWalletOperation("ADD");
    setWalletNote("");
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.name);
    setCustomerEmail(customer.email);
    setCustomerPhone(customer.phone);
    setCustomerStatus(customer.status);
    setCustomerUpiId(customer.upiId != null ? String(customer.upiId) : "");
    setIsEditDialogOpen(true);
  };

  const handleView = (customer) => {
    setSelectedCustomer(customer);
    setIsViewDialogOpen(true);
  };

  const handleOpenNotify = (customer) => {
    setNotificationTarget(customer);
    setNotificationDialogOpen(true);
  };

  const handleCloseNotify = () => {
    setNotificationDialogOpen(false);
    setNotificationTarget(null);
  };

  const renderCustomerActions = (customer, { mobile = false } = {}) => (
    <div className={mobile ? "flex flex-wrap gap-2" : "flex items-center justify-end gap-1"}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleView(customer)}
        className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 h-8 px-2"
      >
        <Eye className="h-3 w-3 mr-1" />
        View
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleOpenWalletSidebar(customer)}
        className={`border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 h-8 px-2 ${mobile ? "flex-1" : ""}`}
      >
        <Wallet className="h-3 w-3 mr-1" />
        Wallet
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleOpenNotify(customer)}
        className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 h-8 px-2"
      >
        <Bell className="h-3 w-3 mr-1" />
        Notify
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleEdit(customer)}
        className={`border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 h-8 px-2 ${mobile ? "flex-1" : ""}`}
      >
        <Edit className="h-3 w-3 mr-1" />
        Edit
      </Button>
    </div>
  );

  const resetForm = () => {
    setCustomerName("");
    setCustomerEmail("");
    setCustomerPhone("");
    setCustomerStatus("ACTIVE");
    setCustomerUpiId("");
    setCustomerWalletAmount("");
    setWalletOperation("ADD");
    setWalletNote("");
    setSelectedCustomer(null);
  };

  // Statistics calculations
  const activeCustomers = customers.filter(c => c.status === "ACTIVE").length;
  const inactiveCustomers = customers.filter(c => c.status === "DELETED").length;
  const totalWishes = customers.reduce((sum, c) => sum + (c.wishCounts?.total || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header Section */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Customer Management</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Manage and analyze customer information across your platform
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setNotificationTarget(null);
                  setNotificationDialogOpen(true);
                }}
              >
                <Bell className="h-4 w-4 mr-2" />
                Send Notification
              </Button>
              <Button 
                onClick={() => setIsCreateDialogOpen(true)}
                className="bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
              >
              <Plus className="h-4 w-4 mr-2" />
              Add Customer
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Customers</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalItems.toLocaleString()}</p>
                </div>
                <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Customers</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{activeCustomers}</p>
                </div>
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Wishes</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalWishes.toLocaleString()}</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">This Page</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{currentCustomers.length}</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Table Card */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm">
          <CardHeader className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">Customer List</CardTitle>
                <CardDescription className="text-sm text-gray-600 dark:text-gray-400">
                  {totalItems === 0
                    ? "No customers match your filters"
                    : `Showing ${startIndex + 1} to ${endIndex} of ${totalItems} customers`}
                  {isFetching && !isLoading ? " · Updating…" : ""}
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search customers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="ALL">All Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="DELETED">Deleted</option>
                </select>
                <Button variant="outline" size="sm" className="border-gray-300 dark:border-gray-600">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-600 dark:text-gray-400" />
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading customers...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Error loading customers</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">There was an error loading the customers. Please try again.</p>
                <Button 
                  onClick={() => refetch()}
                  className="bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                >
                  Retry
                </Button>
              </div>
            ) : customers && customers.length > 0 ? (
              <div>
                {/* Desktop Table */}
              <div className="overflow-x-auto">
                <Table className="hidden lg:table">
                  <TableHeader>
                    <TableRow className="bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <TableHead className="font-semibold text-gray-900 dark:text-white w-16">Sr. No</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Name</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Number</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Status</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Date</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Platform & Version</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentCustomers.map((customer) => (
                      <TableRow 
                        key={customer._id} 
                        className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                      >
                        <TableCell className="py-3">
                          <p className="font-medium text-gray-900 dark:text-white text-sm">
                            {getCustomerName(customer)}
                          </p>
                        </TableCell>
                        <TableCell className="py-3 text-sm text-gray-900 dark:text-white">
                          {getCustomerPhone(customer)}
                        </TableCell>
                        <TableCell className="py-3">
                          <StatusBadge status={customer.status} />
                        </TableCell>
                        <TableCell className="py-3 text-sm text-gray-900 dark:text-white">
                          {formatRegistrationDate(customer.timestamp)}
                        </TableCell>
                        <TableCell className="py-3 text-sm text-gray-900 dark:text-white">
                          <div>{formatPlatform(customer.deviceType)}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {formatAppVersion(customer)}
                          </div>
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          {renderCustomerActions(customer)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

                            {/* Mobile Cards */}
              <div className="lg:hidden space-y-4 p-4">
                {currentCustomers.map((customer) => (
                  <Card key={customer._id} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {getCustomerName(customer)}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {getCustomerPhone(customer)}
                          </p>
                        </div>
                        <StatusBadge status={customer.status} />
                      </div>
                      <div className="space-y-1 mb-3 text-sm text-gray-500 dark:text-gray-400">
                        <p>Registered: {formatRegistrationDate(customer.timestamp)}</p>
                        <p>
                          {formatPlatform(customer.deviceType)} · {formatAppVersion(customer)}
                        </p>
                      </div>
                      {renderCustomerActions(customer, { mobile: true })}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
                    <span>
                      Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} results
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToFirstPage}
                      disabled={currentPage === 1}
                      className="border-gray-300 dark:border-gray-600"
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                      className="border-gray-300 dark:border-gray-600"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => goToPage(pageNum)}
                            className={`w-8 h-8 p-0 ${
                              currentPage === pageNum 
                                ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900" 
                                : "border-gray-300 dark:border-gray-600"
                            }`}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className="border-gray-300 dark:border-gray-600"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToLastPage}
                      disabled={currentPage === totalPages}
                      className="border-gray-300 dark:border-gray-600"
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No customers found</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">Get started by adding your first customer</p>
                <Button 
                  onClick={() => setIsCreateDialogOpen(true)}
                  className="bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Customer
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Customer Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="w-[95vw] max-w-md sm:max-w-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">Add New Customer</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Add a new customer to your database
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Customer Name</label>
              <Input
                placeholder="Enter customer name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="mt-1 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Email Address</label>
              <Input
                type="email"
                placeholder="Enter email address"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                className="mt-1 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
              <Input
                placeholder="Enter phone number"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                className="mt-1 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
              <select
                value={customerStatus}
                onChange={(e) => setCustomerStatus(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setIsCreateDialogOpen(false);
                resetForm();
              }}
              className="w-full sm:w-auto border-gray-300 dark:border-gray-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCustomer}
              className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              Create Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Customer Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="w-[95vw] max-w-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Customer Details
            </DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              {selectedCustomer ? getCustomerName(selectedCustomer) : "Customer information"}
            </DialogDescription>
          </DialogHeader>
          {selectedCustomer && (
            <div className="max-h-[60vh] overflow-y-auto pr-1">
              <DetailRow label="Customer ID" value={selectedCustomer._id || selectedCustomer.id} mono />
              <DetailRow label="Name" value={getCustomerName(selectedCustomer)} />
              <DetailRow label="Email" value={selectedCustomer.email || "—"} />
              <DetailRow label="Phone" value={getCustomerPhone(selectedCustomer)} />
              <DetailRow label="Status" value={selectedCustomer.status || "—"} />
              <DetailRow label="Gender" value={selectedCustomer.gender || "—"} />
              <DetailRow
                label="UPI ID"
                value={
                  selectedCustomer.upiId && String(selectedCustomer.upiId).trim()
                    ? selectedCustomer.upiId
                    : "—"
                }
                mono
              />
              <DetailRow label="UPI change count" value={selectedCustomer.upiIdUpdateCount ?? 0} />
              <DetailRow
                label="Wallet balance"
                value={formatWallet(selectedCustomer.walletBalance ?? 0)}
              />
              <DetailRow
                label="Registration date"
                value={formatRegistrationDate(selectedCustomer.timestamp)}
              />
              <DetailRow
                label="Last active"
                value={formatLastActive(selectedCustomer.lastActiveAt)}
              />
              <DetailRow label="Platform" value={formatPlatform(selectedCustomer.deviceType)} />
              <DetailRow label="App version" value={formatAppVersion(selectedCustomer)} />
              <DetailRow label="Total wishes" value={selectedCustomer.wishCounts?.total ?? 0} />
              <DetailRow label="Used wishes" value={selectedCustomer.wishCounts?.used ?? 0} />
              <DetailRow label="Unused wishes" value={selectedCustomer.wishCounts?.unused ?? 0} />
            </div>
          )}
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setIsViewDialogOpen(false)}
              className="w-full sm:w-auto border-gray-300 dark:border-gray-600"
            >
              Close
            </Button>
            {selectedCustomer && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsViewDialogOpen(false);
                    handleOpenNotify(selectedCustomer);
                  }}
                  className="w-full sm:w-auto border-gray-300 dark:border-gray-600"
                >
                  <Bell className="h-4 w-4 mr-2" />
                  Send Notification
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsViewDialogOpen(false);
                    handleOpenWalletSidebar(selectedCustomer);
                  }}
                  className="w-full sm:w-auto border-gray-300 dark:border-gray-600"
                >
                  <Wallet className="h-4 w-4 mr-2" />
                  Wallet
                </Button>
                <Button
                  onClick={() => {
                    setIsViewDialogOpen(false);
                    handleEdit(selectedCustomer);
                  }}
                  className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit UPI
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="w-[95vw] max-w-md sm:max-w-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">Edit Customer</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Profile fields are view-only. Edit UPI for payouts; each saved change increments the UPI change counter when the value differs.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Customer Name</label>
              <Input
                placeholder="Enter customer name"
                value={customerName}
                readOnly
                className="mt-1 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-900/50"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Email Address</label>
              <Input
                type="email"
                placeholder="Enter email address"
                value={customerEmail}
                readOnly
                className="mt-1 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-900/50"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
              <Input
                placeholder="Enter phone number"
                value={customerPhone}
                readOnly
                className="mt-1 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-900/50"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
              <select
                value={customerStatus}
                disabled
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-900/50 text-gray-900 dark:text-white text-sm"
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                UPI ID (editable)
              </label>
              <Input
                placeholder="e.g. name@bank or mobile@paytm"
                value={customerUpiId}
                onChange={(e) => setCustomerUpiId(e.target.value)}
                className="mt-1 font-mono border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              />
              {selectedCustomer && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  UPI change count (before save): {selectedCustomer.upiIdUpdateCount ?? 0}
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setIsEditDialogOpen(false);
                resetForm();
              }}
              className="w-full sm:w-auto border-gray-300 dark:border-gray-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleEditCustomer}
              disabled={isSavingUpi || !selectedCustomer?._id}
              className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              {isSavingUpi ? (
                <>
                  <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
                  Saving…
                </>
              ) : (
                "Save UPI"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wallet balance update popup */}
      <Dialog open={isWalletUpdateOpen} onOpenChange={(open) => !open && handleCloseWalletUpdate()}>
        <DialogContent className="w-[95vw] max-w-md sm:max-w-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Adjust wallet balance
            </DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Add money to the wallet or deduct from it. Recorded as Admin in transaction history.
            </DialogDescription>
          </DialogHeader>
          {walletCustomer && (
            <div className="space-y-4">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800/80 px-4 py-3 border border-gray-200 dark:border-gray-700">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {walletCustomer.name || walletCustomer.phone || "Customer"}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Current available:{" "}
                  {formatWallet(walletData?.balance ?? walletCustomer.walletBalance ?? 0)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Action
                </label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={walletOperation === "ADD" ? "default" : "outline"}
                    className={
                      walletOperation === "ADD"
                        ? "bg-green-600 hover:bg-green-700 text-white"
                        : "border-gray-300 dark:border-gray-600"
                    }
                    onClick={() => setWalletOperation("ADD")}
                  >
                    Add to wallet
                  </Button>
                  <Button
                    type="button"
                    variant={walletOperation === "DEDUCT" ? "default" : "outline"}
                    className={
                      walletOperation === "DEDUCT"
                        ? "bg-red-600 hover:bg-red-700 text-white"
                        : "border-gray-300 dark:border-gray-600"
                    }
                    onClick={() => setWalletOperation("DEDUCT")}
                  >
                    Deduct from wallet
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Amount (₹)
                </label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={customerWalletAmount}
                  onChange={(e) => setCustomerWalletAmount(e.target.value)}
                  className="mt-1 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                />
                {(() => {
                  const current = Number(walletData?.balance ?? walletCustomer.walletBalance ?? 0);
                  const amt = parseFloat(String(customerWalletAmount).trim());
                  if (!Number.isFinite(amt) || amt <= 0) return null;
                  const next =
                    walletOperation === "ADD"
                      ? Math.round((current + amt) * 100) / 100
                      : Math.round((current - amt) * 100) / 100;
                  return (
                    <p
                      className={`text-xs mt-2 ${
                        next < 0
                          ? "text-red-600 dark:text-red-400"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      New balance will be: {formatWallet(Math.max(0, next))}
                      {next < 0 ? " (insufficient balance)" : ""}
                    </p>
                  );
                })()}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Note (shown in app) <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="Reason for adjustment — user will see this in wallet"
                  value={walletNote}
                  onChange={(e) => setWalletNote(e.target.value)}
                  className="mt-1 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This note appears on the user’s My Wallet transaction.
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleCloseWalletUpdate} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button
              onClick={handleUpdateWallet}
              disabled={
                isSavingWallet ||
                !walletCustomer?._id ||
                !String(walletNote || "").trim()
              }
              className={`w-full sm:w-auto ${
                walletOperation === "DEDUCT"
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-green-600 hover:bg-green-700 text-white"
              }`}
            >
              {isSavingWallet ? (
                <>
                  <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
                  Updating…
                </>
              ) : walletOperation === "DEDUCT" ? (
                "Deduct from wallet"
              ) : (
                "Add to wallet"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustomerWalletSidebar
        open={walletSidebarOpen}
        onClose={handleCloseWalletSidebar}
        customer={walletCustomer}
        walletData={walletData}
        isLoading={walletLoading}
        isFetching={walletFetching}
        error={walletError}
        onRefresh={refetchWallet}
        onUpdateBalance={handleOpenWalletUpdate}
      />

      <SendNotificationDialog
        open={notificationDialogOpen}
        onOpenChange={(open) => !open && handleCloseNotify()}
        initialUserIds={notificationTarget ? [notificationTarget._id] : []}
        initialUsers={notificationTarget ? [notificationTarget] : []}
        dialogTitle={
          notificationTarget
            ? `Notify ${getCustomerName(notificationTarget)}`
            : "Send Push Notification"
        }
        description={
          notificationTarget
            ? "Send a push notification to this customer on the mobile app"
            : "Send a push notification to selected users or all users"
        }
      />

    </div>
  );
};

export default Customers;
